import { IBalusterGeometry, IRailingGeometry, IStairConfig, IStepGeometry, ISupportPosts } from '@stair-craft/common/util';

import { RISER_BOARD_THICKNESS, STRINGER_THICKNESS, STRINGER_WIDTH } from '../../constants';
import { railingVolume } from '../shared-railing-wood/railing-wood';
import { IStairWoodEstimate, stairWoodEstimate } from '../shared-stair-wood/stair-wood';

/**
 * Wood volume / weight / cost for a two-stringer turned stair (L or U): winder average-depth
 * treads and landing boards, risers, both stringers and railing members. Each part is weighed
 * and priced by its own species in `stairWoodEstimate`.
 */
export function calculateMaterialVolume(
    allSteps: IStepGeometry[],
    stepCount: number,
    riserHeight: number,
    flight1MarchLen: number,
    flight2MarchLen: number,
    railings: IRailingGeometry[],
    config: IStairConfig,
    supports?: ISupportPosts
): IStairWoodEstimate {
    // Treads (including landing and winders)
    let treadVolume: number = 0;
    for (const step of allSteps) {
        if (step.isWinder) {
            const avgDepth: number = ((step.innerDepth ?? 0) + (step.outerDepth ?? step.treadDepth)) / 2;
            treadVolume += (step.treadWidth / 1000) * (avgDepth / 1000) * (step.treadThickness / 1000);
        } else {
            treadVolume += (step.treadWidth / 1000) * (step.treadDepth / 1000) * (step.treadThickness / 1000);
        }
    }

    // Risers (stepCount risers total)
    const riserBoardHeight: number = riserHeight - config.treadThickness;
    const riserVolume: number = stepCount * (config.stairWidth / 1000) * (riserBoardHeight / 1000) * (RISER_BOARD_THICKNESS / 1000);

    // Stringers (both flights)
    const stringerCount: number = 2;
    const totalStringerLen: number = (flight1MarchLen + flight2MarchLen) / 1000;
    // Support posts of the turn go by the load-bearing species (SC-CALC-093).
    const supportVolume: number = (supports?.posts ?? []).reduce(
        (sum: number, post: IBalusterGeometry) => sum + (supports?.width ?? 0) ** 2 * post.height,
        0
    );
    const stringerVolume: number =
        stringerCount * totalStringerLen * (STRINGER_THICKNESS / 1000) * (STRINGER_WIDTH / 1000) + supportVolume / 1000 ** 3;

    return stairWoodEstimate(
        { tread: treadVolume, riser: riserVolume, stringer: stringerVolume, railing: railingVolume(railings, config.railing) },
        config
    );
}
